/**
 * A patient storefront and the staff app can be open in the same browser at
 * once — a pharmacist testing checkout on their own work machine, for
 * instance. api/client.ts's `tokenStore` is a single shared key; a patient
 * OTP login writing to that same key would silently clobber a staff session
 * (or vice versa). This is a fully separate token store and request path for
 * exactly that reason — the two never share storage or an Authorization
 * header.
 */
import { ApiError, type ApiEnvelope } from '@/api/client';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';
const PATIENT_TOKEN_KEY = 'dawa.patient.token';

export const patientTokenStore = {
  get: (): string | null => {
    try {
      return localStorage.getItem(PATIENT_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string) => {
    try {
      localStorage.setItem(PATIENT_TOKEN_KEY, token);
    } catch {
      /* private mode — session lives in memory for this tab only */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(PATIENT_TOKEN_KEY);
    } catch {
      /* nothing to do */
    }
  },
};

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Skip attaching the patient token — for the public catalog, which is browsable before login. */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function patientRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (!anonymous) {
    const token = patientTokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('Cannot reach the store. Check your connection.', 0, 'NETWORK');
  }

  if (response.status === 401) {
    patientTokenStore.clear();
    throw new ApiError('Your session has expired. Please log in again.', 401, 'UNAUTHORIZED');
  }

  if (response.status === 204) return undefined as T;

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(
      response.ok ? 'The server sent a response we could not read.' : `Request failed (${response.status}).`,
      response.status,
      'BAD_RESPONSE',
    );
  }

  if (!response.ok || !envelope.success) {
    throw new ApiError(envelope.message ?? `Request failed (${response.status}).`, response.status, envelope.code, envelope.errors);
  }

  return envelope.data as T;
}

export const patientApi = {
  get: <T>(path: string, query?: RequestOptions['query'], anonymous?: boolean) =>
    patientRequest<T>(path, { method: 'GET', query, anonymous }),
  post: <T>(path: string, body?: unknown, anonymous?: boolean) =>
    patientRequest<T>(path, { method: 'POST', body, anonymous }),
};
